import { type ReactElement, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fontFamilies, s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };
type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

type ProjectDataWipeSectionProps = {
  project: ProjectDto | null;
  deleteProject: {
    isPending: boolean;
    mutate: (
      input: { id: string },
      options?: {
        onSuccess?: () => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  wipeProjectData: {
    isPending: boolean;
    mutate: (
      input: {
        id: string;
        confirmation: string;
        reasonCode?: string;
        ticket?: string;
        async?: boolean;
      },
      options?: {
        onSuccess?: (result: { status: 'wiped' | 'scheduled'; jobId?: string }) => void;
        onError?: (err: unknown) => void;
      },
    ) => void;
  };
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
  onProjectDeleted: () => void;
};

export const ProjectDataWipeSection = ({
  project,
  deleteProject,
  wipeProjectData,
  mutationError,
  notify,
  onProjectDeleted,
}: ProjectDataWipeSectionProps): ReactElement => {
  const projectNameCodeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: fontFamilies.mono,
    backgroundColor: 'action.hover',
    borderRadius: s('xxs'),
    pl: s('sm'),
    pr: s('xs'),
  } as const;
  const destructiveActionButtonSx = { width: { sm: '120px' } } as const;

  const [wipeOpen, setWipeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [wipeProjectNameInput, setWipeProjectNameInput] = useState('');
  const [deleteProjectNameInput, setDeleteProjectNameInput] = useState('');
  const expectedProjectName = project?.name.trim() ?? '';
  const expectedWipeConfirmation = project ? `WIPE_PROJECT_DATA:${project.id}` : '';
  const wipeProjectNameMatches =
    expectedProjectName.length > 0 && wipeProjectNameInput.trim() === expectedProjectName;
  const deleteProjectNameMatches =
    expectedProjectName.length > 0 && deleteProjectNameInput.trim() === expectedProjectName;

  return (
    <Panel title="Project Deletion">
      <Stack gap="sm">
        <Stack gap="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} gap="sm" align={{ sm: 'center' }}>
            <Text role="body" color="textSecondary" sx={{ flex: 1 }}>
              Delete all data associated with this project.
            </Text>
            <Button
              variant="destructive"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || wipeProjectData.isPending || deleteProject.isPending}
              onClick={() => {
                mutationError.clearError();
                setWipeProjectNameInput('');
                setWipeOpen(true);
              }}
            >
              Delete Data
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap="sm" align={{ sm: 'center' }}>
            <Text role="body" color="textSecondary" sx={{ flex: 1 }}>
              Removes the project from your workspace.
            </Text>
            <Button
              variant="destructive"
              fullWidthOnMobile={false}
              sx={destructiveActionButtonSx}
              disabled={!project || deleteProject.isPending || wipeProjectData.isPending}
              onClick={() => {
                mutationError.clearError();
                setDeleteProjectNameInput('');
                setDeleteOpen(true);
              }}
            >
              Delete Project
            </Button>
          </Stack>
        </Stack>
        <ConfirmDialog
          open={wipeOpen}
          title="Delete Project Data?"
          description={
            <Stack gap="md">
              <Text role="body" color="textSecondary">
                This action is permanent and cannot be undone. It will remove all project test data.
              </Text>
              <Text role="body" color="textSecondary">
                Enter{' '}
                <Box component="code" sx={projectNameCodeSx}>
                  {expectedProjectName}
                </Box>{' '}
                to confirm.
              </Text>
              <TextField
                label="Project Name"
                value={wipeProjectNameInput}
                onChange={(event) => {
                  setWipeProjectNameInput(event.target.value);
                }}
                fullWidth
              />
            </Stack>
          }
          confirmLabel="Delete Data"
          confirmColor="error"
          confirmDisabled={!wipeProjectNameMatches}
          loading={wipeProjectData.isPending || deleteProject.isPending}
          onClose={() => {
            if (wipeProjectData.isPending || deleteProject.isPending) return;
            setWipeOpen(false);
            setWipeProjectNameInput('');
          }}
          onConfirm={() => {
            if (!project || !wipeProjectNameMatches) return;
            wipeProjectData.mutate(
              {
                id: project.id,
                confirmation: expectedWipeConfirmation,
                reasonCode: 'USER_REQUEST',
              },
              {
                onSuccess: (result) => {
                  mutationError.clearError();
                  setWipeOpen(false);
                  setWipeProjectNameInput('');
                  if (result.status === 'scheduled') {
                    notify({ message: `Scheduled data wipe for ${project.name}`, tone: 'blue' });
                    return;
                  }
                  notify({ message: `Wiped data for ${project.name}`, tone: 'green' });
                },
                onError: (err) => {
                  mutationError.setError(err);
                },
              },
            );
          }}
        />
        <ConfirmDialog
          open={deleteOpen}
          title="Delete Project?"
          description={
            <Stack gap="md">
              <Text role="body" color="textSecondary">
                This action is permanent and cannot be undone.
              </Text>
              <Text role="body" color="textSecondary">
                Enter{' '}
                <Box component="code" sx={projectNameCodeSx}>
                  {expectedProjectName}
                </Box>{' '}
                to confirm.
              </Text>
              <TextField
                label="Project Name"
                value={deleteProjectNameInput}
                onChange={(event) => {
                  setDeleteProjectNameInput(event.target.value);
                }}
                fullWidth
              />
            </Stack>
          }
          confirmLabel="Delete Project"
          confirmColor="error"
          confirmDisabled={!deleteProjectNameMatches}
          loading={deleteProject.isPending || wipeProjectData.isPending}
          onClose={() => {
            if (deleteProject.isPending || wipeProjectData.isPending) return;
            setDeleteOpen(false);
            setDeleteProjectNameInput('');
          }}
          onConfirm={() => {
            if (!project || !deleteProjectNameMatches) return;
            deleteProject.mutate(
              { id: project.id },
              {
                onSuccess: () => {
                  mutationError.clearError();
                  setDeleteOpen(false);
                  setDeleteProjectNameInput('');
                  notify({ message: `Deleted project ${project.name}`, tone: 'green' });
                  onProjectDeleted();
                },
                onError: (err) => {
                  mutationError.setError(err);
                },
              },
            );
          }}
        />
      </Stack>
    </Panel>
  );
};
